import { useState, type ReactNode } from "react";
import {
  AlertTriangle, Archive, Bell, Boxes, ChevronRight, ClipboardClock, FileUp, History,
  LayoutDashboard, Menu, Moon, PackagePlus, RefreshCw, Settings, Sun, Users, X,
} from "lucide-react";
import type { AppSettings, AppUser } from "../types";

export type PageKey = "dashboard" | "estoque" | "produtos" | "movimentacoes" | "importacoes" | "alertas" | "historico" | "sincronizacao" | "usuarios" | "configuracoes";

const navigation: Array<{ key: PageKey; label: string; icon: typeof LayoutDashboard }> = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "estoque", label: "Estoque", icon: Boxes },
  { key: "produtos", label: "Produtos", icon: Archive },
  { key: "movimentacoes", label: "Movimentações", icon: ClipboardClock },
  { key: "importacoes", label: "Importações", icon: FileUp },
  { key: "alertas", label: "Alertas", icon: Bell },
  { key: "historico", label: "Histórico", icon: History },
  { key: "sincronizacao", label: "Sincronização", icon: RefreshCw },
  { key: "usuarios", label: "Usuários", icon: Users },
  { key: "configuracoes", label: "Configurações", icon: Settings },
];

interface LayoutProps {
  page: PageKey;
  onPage: (page: PageKey) => void;
  settings?: AppSettings;
  user: AppUser;
  nativeAndroid: boolean;
  pendingCount: number;
  syncLabel: string;
  onSync: () => void;
  onQuickMovement: () => void;
  onTheme: () => void;
  onSignOut: () => void;
  children: ReactNode;
}

export function Layout(props: LayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const current = navigation.find((item) => item.key === props.page)!;
  const go = (page: PageKey) => { props.onPage(page); setMobileOpen(false); };
  const dark = document.documentElement.dataset.theme === "dark";

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileOpen ? "sidebar-open" : ""}`}>
        <div className="brand-block">
          <img src="/logo-wg.jpeg" alt="WG Tricot" className="brand-logo" />
          <div><strong>WG Tricot</strong><span>Controle de estoque</span></div>
          <button className="icon-button sidebar-close" onClick={() => setMobileOpen(false)} aria-label="Fechar menu"><X size={20} /></button>
        </div>
        <nav className="main-nav" aria-label="Menu principal">
          {navigation.map(({ key, label, icon: Icon }) => (
            <button key={key} className={props.page === key ? "active" : ""} onClick={() => go(key)}>
              <Icon size={19} /><span>{label}</span>{props.page === key && <ChevronRight size={15} className="nav-chevron" />}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="user-avatar">{props.user.name.charAt(0)}</div>
          <div><strong>{props.user.name}</strong><span>{props.nativeAndroid ? "Aplicativo Android" : props.user.role === "administrador" ? "Administrador" : "Operador"}</span></div>
          {!props.nativeAndroid && <button className="text-button" onClick={props.onSignOut}>Sair</button>}
        </div>
      </aside>
      {mobileOpen && <button className="sidebar-scrim" aria-label="Fechar menu" onClick={() => setMobileOpen(false)} />}

      <div className="workspace">
        <header className="topbar">
          <div className="topbar-title">
            <button className="icon-button menu-button" onClick={() => setMobileOpen(true)} aria-label="Abrir menu"><Menu size={21} /></button>
            <div><span>WG Tricot</span><h1>{current.label}</h1></div>
          </div>
          <div className="topbar-actions">
            <button className="connection-button" onClick={props.onSync} title="Sincronizar agora">
              <span className={`connection-dot ${navigator.onLine ? "online" : "offline"}`} />
              <span className="connection-copy">{navigator.onLine ? props.syncLabel : "Trabalhando offline"}</span>
              {props.pendingCount > 0 && <b>{props.pendingCount}</b>}
            </button>
            <button className="icon-button" onClick={props.onTheme} aria-label="Alternar tema">{dark ? <Sun size={20} /> : <Moon size={20} />}</button>
            <button className="avatar-button" title={props.user.name}>{props.user.name.charAt(0)}</button>
          </div>
        </header>

        <main className="content">{props.children}</main>
        <button className="quick-fab" onClick={props.onQuickMovement}><PackagePlus size={20} /> <span>Movimentação</span></button>

        <nav className="mobile-nav" aria-label="Navegação móvel">
          {navigation.slice(0, 5).map(({ key, label, icon: Icon }) => (
            <button key={key} className={props.page === key ? "active" : ""} onClick={() => go(key)}>
              <Icon size={20} /><span>{label === "Movimentações" ? "Movim." : label}</span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}

export function StatusPill({ tone, children }: { tone: "success" | "warning" | "danger" | "neutral" | "info"; children: ReactNode }) {
  return <span className={`status-pill ${tone}`}>{children}</span>;
}

export function EmptyState({ icon: Icon = AlertTriangle, title, text, action }: { icon?: typeof AlertTriangle; title: string; text: string; action?: ReactNode }) {
  return <div className="empty-state"><span className="empty-icon"><Icon size={26} /></span><h3>{title}</h3><p>{text}</p>{action}</div>;
}
